import type { CriterioAnaliseView, SolicitacaoAnalise } from "../../../../services";
import {
  listarAnalisesCriterio,
  pickAnaliseLinhaEdicaoParecer,
} from "../../../../domain/shipping/analiseCriterioList";
import type { AnalisePorCriterio } from "../../../../services";


export type AnaliseCriterioPatchRow = {
  id: number;
  criterioId: number;
  observacaoAnalista?: string | null;
  observacaoInterna?: string | null;
  statusId: number;
};

function analiseCompletaComGrupos(x: unknown): x is SolicitacaoAnalise {
  if (!x || typeof x !== "object") return false;
  const g = (x as SolicitacaoAnalise).grupos;
  return Array.isArray(g) && g.length > 0;
}


export function extrairSolicitacaoAnaliseDaResposta(
  payload: unknown,
): SolicitacaoAnalise | null {
  if (!payload || typeof payload !== "object") return null;
  const obj = payload as Record<string, unknown>;

  const candidatoDireto = obj as Partial<SolicitacaoAnalise>;
  if (analiseCompletaComGrupos(candidatoDireto)) {
    return candidatoDireto as SolicitacaoAnalise;
  }

  const nested =
    (obj.analise as unknown) ??
    (obj.data as unknown) ??
    (obj.result as unknown) ??
    (obj.payload as unknown) ??
    null;

  if (nested && typeof nested === "object" && analiseCompletaComGrupos(nested)) {
    return nested as SolicitacaoAnalise;
  }

  if (
    nested &&
    typeof nested === "object" &&
    analiseCompletaComGrupos((nested as Record<string, unknown>).data)
  ) {
    return (nested as { data: SolicitacaoAnalise }).data;
  }

  return null;
}

function coletarRowsAnaliseCriterio(payload: unknown): AnaliseCriterioPatchRow[] | null {
  if (!payload || typeof payload !== "object") return null;
  const o = payload as Record<string, unknown>;
  const rows = o.analiseCriterioSolicitacao;
  if (!Array.isArray(rows)) return null;
  const out: AnaliseCriterioPatchRow[] = [];
  for (const x of rows) {
    if (!x || typeof x !== "object") continue;
    const r = x as Record<string, unknown>;
    const id = Number(r.id);
    const criterioId = Number(r.criterioId);
    const statusId = Number(r.statusId);
    if (!Number.isFinite(criterioId) || criterioId <= 0) continue;
    if (!Number.isFinite(statusId)) continue;
    out.push({
      id: Number.isFinite(id) && id > 0 ? id : 0,
      criterioId,
      statusId,
      observacaoAnalista: r.observacaoAnalista as string | null | undefined,
      observacaoInterna: r.observacaoInterna as string | null | undefined,
    });
  }
  return out.length > 0 ? out : null;
}

function atualizarCriterioComRow(
  criterio: CriterioAnaliseView,
  row: AnaliseCriterioPatchRow,
): CriterioAnaliseView {
  const arr = listarAnalisesCriterio(criterio.analise);
  const prim = pickAnaliseLinhaEdicaoParecer(criterio.analise);
  const statusObj = { id: row.statusId, nome: "" };

  const base: AnalisePorCriterio = prim
    ? {
        ...prim,
        id: row.id > 0 ? row.id : prim.id,
      }
    : {
        id: row.id > 0 ? row.id : 0,
        solicitadoEm: null,
        adequadoEm: null,
        observacaoAnalista: null,
        observacaoSindicato: null,
        observacaoInterna: null,
        status: statusObj,
      };

  const merged: AnalisePorCriterio = {
    ...base,
    id: row.id > 0 ? row.id : base.id,
    observacaoAnalista:
      row.observacaoAnalista !== undefined && row.observacaoAnalista !== null
        ? row.observacaoAnalista
        : base.observacaoAnalista,
    observacaoInterna:
      row.observacaoInterna !== undefined && row.observacaoInterna !== null
        ? row.observacaoInterna
        : base.observacaoInterna,
    status: statusObj,
  };

  if (arr.length <= 1) {
    return { ...criterio, analise: merged };
  }

  const idx = prim ? arr.indexOf(prim) : -1;
  const replaceAt = idx >= 0 ? idx : arr.length - 1;
  const nextArr = arr.map((a, i) => (i === replaceAt ? merged : a));
  return { ...criterio, analise: nextArr };
}


export function mergeAnaliseComRowsPatch(
  anterior: SolicitacaoAnalise,
  rows: AnaliseCriterioPatchRow[],
): SolicitacaoAnalise {
  const byCriterio = new Map<number, AnaliseCriterioPatchRow>();
  for (const r of rows) {
    byCriterio.set(r.criterioId, r);
  }

  const grupos = (anterior.grupos || []).map((g) => ({
    ...g,
    criterios: (g.criterios || []).map((c) => {
      const row = byCriterio.get(c.criterioId) ?? byCriterio.get(c.id);
      if (!row) return { ...c };
      return atualizarCriterioComRow(c, row);
    }),
  }));

  return { ...anterior, grupos };
}


export function resolverAnaliseAposPatch(
  anterior: SolicitacaoAnalise | null,
  payload: unknown,
): SolicitacaoAnalise | null {
  const completo = extrairSolicitacaoAnaliseDaResposta(payload);
  if (completo) {
    if (!completo.status && anterior?.status) {
      return { ...completo, status: anterior.status };
    }
    return completo;
  }

  const rows = coletarRowsAnaliseCriterio(payload);
  if (anterior && rows?.length) {
    return mergeAnaliseComRowsPatch(anterior, rows);
  }
  return null;
}
